import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Produtos.module.css';
import ProdutoModal from './ProdutoModal';
import { listar, criar, atualizar, COLECOES } from '../../../services/firestore';

// ====================================================================
// CADASTRO DE PRODUTOS — Bloco 3.5
// Dados do produto -> colecao "produtos".
// Filtros Resina/Marca -> vem das colecoes reais (3.2).
// Coluna Documentos: status booleano (upload real = Bloco 6).
// ====================================================================

function normalizarProduto(p) {
  return {
    ...p,
    cor: p.cor || '',
    resina: p.resina || '',
    orientacao: p.orientacao || '',
    ativo: p.ativo ?? true,
  };
}

export default function Produtos() {
  const navigate = useNavigate();

  const [produtos, setProdutos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroResina, setFiltroResina] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState(null);

  const [opcoesResina, setOpcoesResina] = useState([]);

  // ------------------------------------------------------------------
  // Carregar produtos do Firestore
  // ------------------------------------------------------------------
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const dados = await listar(COLECOES.PRODUTOS, { ordenarPor: 'cor' });
      setProdutos(dados.map(normalizarProduto));
    } catch (e) {
      setErro(e.message || 'Erro ao carregar produtos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const resinas = await listar(COLECOES.RESINAS, { ordenarPor: 'nome' });
        if (!vivo) return;
        setOpcoesResina(resinas.filter((r) => r.ativo).map((r) => r.nome));
      } catch (e) {
        console.error('Erro ao carregar opções de filtro:', e);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const produtosFiltrados = useMemo(() => {
    return produtos.filter((p) => {
      if (busca) {
        const q = busca.toLowerCase();
        const match =
          p.cor.toLowerCase().includes(q) ||
          p.resina.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filtroResina && p.resina !== filtroResina) return false;
      if (filtroStatus === 'Ativos' && !p.ativo) return false;
      if (filtroStatus === 'Inativos' && p.ativo) return false;
      return true;
    });
  }, [produtos, busca, filtroResina, filtroStatus]);

  const totalAtivos = produtos.filter((p) => p.ativo).length;
  const totalInativos = produtos.filter((p) => !p.ativo).length;

  function abrirNovoProduto() {
    setProdutoEditando(null);
    setModalAberto(true);
  }

  function abrirEditarProduto(produto) {
    setProdutoEditando(produto);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setProdutoEditando(null);
  }

  // ------------------------------------------------------------------
  // Salvar (criar / atualizar) no Firestore
  // ------------------------------------------------------------------
  async function salvarProduto(dadosProduto) {
    setSalvando(true);
    setErro('');
    try {
      const limpo = { ...dadosProduto };
      delete limpo.id;
      if (produtoEditando) {
        await atualizar(COLECOES.PRODUTOS, produtoEditando.id, limpo);
      } else {
        await criar(COLECOES.PRODUTOS, { ...limpo, ativo: limpo.ativo ?? true });
      }
      fecharModal();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao salvar o produto.');
    } finally {
      setSalvando(false);
    }
  }

  // ------------------------------------------------------------------
  // Ativar / Inativar (otimista com rollback)
  // ------------------------------------------------------------------
  async function toggleAtivoProduto(id) {
    const alvo = produtos.find((p) => p.id === id);
    if (!alvo) return;
    setErro('');
    setProdutos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ativo: !p.ativo } : p))
    );
    try {
      await atualizar(COLECOES.PRODUTOS, id, { ativo: !alvo.ativo });
    } catch (e) {
      setErro(e.message || 'Erro ao alterar o status do produto.');
      setProdutos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ativo: alvo.ativo } : p))
      );
    }
  }

  return (
    <>
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Cadastro de Produtos</div>
        </div>
        <button className={styles.btnVoltar} onClick={() => navigate('/cadastros')}>
          ← Voltar para Cadastros
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar cor, resina..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroResina}
          onChange={(e) => setFiltroResina(e.target.value)}
        >
          <option value="">Todas as resinas</option>
          {opcoesResina.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          className={styles.toolbarSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="Ativos">Ativos</option>
          <option value="Inativos">Inativos</option>
        </select>
        <button className={styles.btnNovo} onClick={abrirNovoProduto}>
          + Novo produto
        </button>
      </div>

      {/* Tabela */}
      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {produtos.length} produtos cadastrados ({totalAtivos} ativos ·{' '}
            {totalInativos}{' '}
            {totalInativos === 1 ? 'inativo' : 'inativos'})
          </span>
          <span className={styles.tabelaHint}>Clique em ✏️ para editar</span>
        </div>

        <div className={styles.colHeader}>
          <div>Produto</div>
          <div>Resina</div>
          <div></div>
        </div>

        {carregando ? (
          <div className={styles.semResultados}>Carregando produtos...</div>
        ) : produtosFiltrados.length === 0 ? (
          <div className={styles.semResultados}>
            {produtos.length === 0
              ? 'Nenhum produto cadastrado ainda. Clique em "+ Novo produto" para começar.'
              : 'Nenhum produto encontrado com os filtros aplicados.'}
          </div>
        ) : (
          produtosFiltrados.map((produto) => {
            return (
              <div
                key={produto.id}
                className={`${styles.prodRow} ${!produto.ativo ? styles.inativo : ''}`}
              >
                <div>
                  <div className={styles.prodNome}>{produto.cor}</div>
                  <div className={styles.prodCorMeta}>{produto.resina}</div>
                </div>
                <div className={styles.prodInfoSecundaria}>
                  {produto.resina}
                </div>
                <div className={styles.acoes}>
                  <span
                    className={`${styles.statusTag} ${produto.ativo ? styles.stAtivo : styles.stInativo}`}
                  >
                    {produto.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                  <button
                    className={`${styles.btnAcao} ${styles.editar}`}
                    title="Editar"
                    onClick={() => abrirEditarProduto(produto)}
                  >
                    ✏️
                  </button>
                  <button
                    className={`${styles.btnAcao} ${styles.inativar}`}
                    title={produto.ativo ? 'Inativar' : 'Reativar'}
                    onClick={() => toggleAtivoProduto(produto.id)}
                  >
                    {produto.ativo ? '🚫' : '✅'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {modalAberto && (
        <ProdutoModal
          produto={produtoEditando}
          onSalvar={salvarProduto}
          onCancelar={fecharModal}
          salvando={salvando}
        />
      )}
    </>
  );
}